import { Box, Button, ButtonGroup, Grid, IconButton, Stack, Table, Tooltip } from "@mui/joy";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authenticatedApi } from "api";
import MaestroLoader from "@components/MaestroLoader";
import { ReportDeleteButton } from "@components/reports/ReportDeleteButton";
import { formatDate } from "@utils/dates";
import ObjectColumn from "@components/objects/ObjectColumn";
import { dateColumn, genericColumn, textColumn } from "@api/schema/columnHelpers";
import AbstractObjectRelationship from "@components/objects/AbstractObjectRelationship";
import { useGetAllReports } from "@api/reportService";
// import './Start.scss';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { useDocumentTitle } from "@uidotdev/usehooks";

const recentReportColumns = [
    textColumn({
        label: 'Name',
        column: 'name',
        render: {
            show: (object) => {
                return (<>
                    {object.name}
                    {/* {object.description && <Typography fontSize={'sm'} sx={{fontSize: '0.75rem'}}>{object.description}</Typography>} */}
                </>)
            },
        },
    }),
    textColumn({
        label: 'Created By',
        column: 'created_by',
        render: {
            show: (object) => {
                return (<>
                    {object.created_by ? `${object.created_by.first_name} ${object.created_by.last_name}` : 'N/A'}
                </>)
            },
        },
    }),
    dateColumn({
        label: 'Created At',
        column: 'created_at',
    })
];

function ReportIndex() {
    useDocumentTitle(`Reports | Maestro Community Manager`);

    const navigate = useNavigate();

    const {
        // isPending,
        isLoading,
        isError,
        error,
        data,
        isFetching,
        isPreviousData,
    } = useGetAllReports(
        {
            orderby: 'created_at',
            order: 'desc',
            per_page: '5',
        },
        {
            retry: false,
        }
    );

    const isSearchValid = (search) => {
        // old searches didn't have a community key
        return search.hasOwnProperty('communities');
    }

    const quickReports = [
        {
            name: 'Net New Businesses This Year',
            slug: 'net-new-biz-this-year',
        },
        {
            name: 'Total Assessed Market Value',
            slug: 'total-assessed-market-value',
        },
        {
            name: 'Reinvestment Stats This Month',
            slug: 'reinvestment-stats-this-month',
        },
        {
            name: 'Reinvestment Stats This Quarter',
            slug: 'reinvestment-stats-this-quarter',
        },
        {
            name: 'Reinvestment Stats This Year',
            slug: 'reinvestment-stats-this-year',
        },
        {
            name: 'Reinvestment Stats All Time',
            slug: 'reinvestment-stats-all-time',
        },
        {
            name: 'All Property Owners',
            slug: 'all-property-owners',
        },
        {
            name: 'All Business Owners',
            slug: 'all-business-owners',
        },
        {
            name: 'All Active Businesses',
            slug: 'active-businesses',
        },
        {
            name: 'Veteran Demographic',
            slug: 'veteran-demographic',
        },
        {
            name: 'Minority Demographic',
            slug: 'minority-demographic',
        },
        {
            name: 'Woman Demographic',
            slug: 'female-demographic',
        },
        {
            name: 'LGBT Demographic',
            slug: 'lgbt-demographic',
        },
    ];
    return (<>

    <Box sx={{p: 2}}>

    <Grid container columns={12} gap={2}>
        <ObjectColumn size={{xs: 12, md: 6}}>
            <AbstractObjectRelationship title="Recently Saved Reports">
                {isLoading === true && (
                    <Box alignSelf={'center'}>
                        <MaestroLoader />
                    </Box>
                )}
                {isLoading !== true && (<>
                    {!!data?.data && (
                        <Table
                            stripe={'even'}
                            sx={{
                                margin: 0,
                                padding: 0,
                                tableLayout: 'auto',
                            }}
                        >
                            <thead>
                            <tr>
                                <th>Name</th>
                                <th>Created By</th>
                                <th>Created At</th>
                                <th></th>
                            </tr>
                            </thead>
                            <tbody>
                            {data.data.map((report, index) => (
                                <tr key={index}>
                                    <td>{report.name}</td>
                                    <td>{report.created_by ? `${report.created_by.first_name} ${report.created_by.last_name}` : 'N/A'}</td>
                                    <td>{formatDate(report.created_at)}</td>
                                    <td style={{textAlign: 'right'}}>
                                        <ButtonGroup
                                            color="neutral"
                                            variant="outlined"
                                            size="sm"
                                            sx={{
                                                '--IconButton-size': '1.5rem',
                                                bgcolor: 'background.surface',
                                                verticalAlign: 'sub',
                                                display: 'inline-flex',
                                                // ml: 0.5,
                                                'svg': {
                                                    width: '1.05rem',
                                                    height: '1.05rem',
                                                }
                                            }}
                                        >
                                            {report.searches && isSearchValid(report.searches) && (
                                                <Tooltip title={'Run Search'}>
                                                    <IconButton
                                                        color="neutral"
                                                        variant="outlined"
                                                        size="sm"
                                                        sx={{
                                                            '--IconButton-size': '1.5rem',
                                                            bgcolor: 'background.surface',
                                                            verticalAlign: 'sub',
                                                            // ml: 0.5,
                                                            'svg': {
                                                                width: '1.05rem',
                                                                height: '1.05rem',
                                                            }
                                                        }}
                                                        onClick={() => {
                                                            navigate('./new', { state: { savedSearch: report.searches } });
                                                        }}
                                                    >
                                                        <LaunchRoundedIcon/>
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {report.files && report.files.length > 0 && (
                                                <Tooltip title={'Download PDF'}>
                                                    <IconButton
                                                        color="neutral"
                                                        variant="outlined"
                                                        size="sm"
                                                        sx={{
                                                            '--IconButton-size': '1.5rem',
                                                            bgcolor: 'background.surface',
                                                            verticalAlign: 'sub',
                                                            // ml: 0.5,
                                                            'svg': {
                                                                width: '1.05rem',
                                                                height: '1.05rem',
                                                            }
                                                        }}
                                                        component="a"
                                                        href={report.files[(report.files.length - 1)].url}
                                                        target="_blank"
                                                    >
                                                        <DownloadRoundedIcon/>
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <ReportDeleteButton report={report} />
                                            {/*
                                            <a className="btn btn-sm btn-danger" ng-click="deleteReport(row)" uib-tooltip="Delete Search" tooltip-placement="left" tooltip-append-to-body="true">
                                                <i className="icon-trash2"></i>
                                            </a>
                                            */}
                                        </ButtonGroup>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    )}

                    {data.data.length === 0 && (
                        <div className="wrapper text-center" style={{padding: '1rem'}}>
                            <em>You don't have any recently saved reports.</em>
                            <br/>
                            <Link to="/reports/new" className="btn btn-default spacer-top">Run New Report</Link>
                            {/* <a href="/saved" className="">Run New Report</a> */}
                        </div>
                    )}
                </>)}
            </AbstractObjectRelationship>
        </ObjectColumn>

        <ObjectColumn size={{xs: 12, md: true}}>
            <AbstractObjectRelationship title="Quick Reports">
            <Box sx={{display: 'inline-flex', flexWrap: 'wrap', gap: 2, p: 2}}>
                    {/* <Grid xs={12} md={6} key={index}> */}
                {quickReports.map((report, index) => (

                    <Button
                    color="neutral"
                        size={'sm'}
                        width={'100%'}
                        rightIcon={<i className="icon-chevron-right"></i>}
                        // TODO: swap to decorator
                        onClick={() => {
                            navigate('./new', { state: { quickReport: report.slug } });
                        }}
                    >
                        {report.name}
                    </Button>
                ))}
                    {/* </Grid> */}
                {quickReports.length === 0 && (
                    <div className="wrapper">
                        <em style={{textAlign: 'center', display: 'block' }}>There are no quick reports available at this time.</em>
                    </div>
                )}
                </Box>
            </AbstractObjectRelationship>
        </ObjectColumn>
    </Grid>
    </Box>
    </>);
}

export default ReportIndex;
